import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000AuthContext } from '../../u2000Core/context/auth/U2000UseAuthContext';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { U3208ROLES } from '../../u3208AiRagDocument/U3208';
import { doSearch, type U3208AiRagCorpusSearchRow } from '../../u3208AiRagDocument/U3208Service';
import { NS } from '../U3203';
import { luHandleAppiError } from '../U3203Service';

export interface U3203CorpusOption {
  id: string;
  code: string;
  title: string;
  systemScope: boolean;
}

export function useU3203CorpusHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { hasRole } = useU2000AuthContext();
  const { t } = useTranslation();
  const toast = useToast();
  const canReadCorpus = hasRole(U3208ROLES.READ);
  const [corpora, setCorpora] = useState<U3203CorpusOption[]>([]);
  const [loading, setLoading] = useState(false);

  const loadCorpora = useCallback(async (): Promise<U3203CorpusOption[]> => {
    if (!canReadCorpus) {
      setCorpora([]);
      return [];
    }
    setLoading(true);
    try {
      const response = await doSearch(u2000AxiosAuthenticated, {
        filter: {
          criterias: [],
          fields: [
            { name: 'id' },
            { name: 'code' },
            { name: 'title' },
            { name: 'active' },
            { name: 'systemScope' },
          ],
          sortedFields: [{ name: 'code', sortOrder: 'ASC' }],
        },
        pagination: { page: 1, size: 500 },
      });
      const rows = (response.data?.results ?? []) as U3208AiRagCorpusSearchRow[];
      const active = rows
        .filter((row) => row.active)
        .map((row) => ({
          id: row.id,
          code: row.code,
          title: row.title,
          systemScope: row.systemScope,
        }));
      setCorpora(active);
      return active;
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setCorpora([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [canReadCorpus, u2000AxiosAuthenticated, t, toast]);

  const corpusLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const corpus of corpora) {
      map[corpus.id] = `${corpus.code} — ${corpus.title}`;
    }
    return map;
  }, [corpora]);

  return { corpora, corpusLabelById, loading, canReadCorpus, loadCorpora };
}
